import React, { useContext, useEffect, useEffectEvent, useMemo } from 'react'
import classNames from 'classnames'

import styles from './styles.module.scss'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import { MediaPlayerMutexContext } from '../../contexts/MediaPlayerMutexContext'
import Icon, { IconButton } from '../Icon'
import { NextVoiceMessagePlayerContext } from '../../contexts/NextVoiceMessagePlayerContext'
import { CustomAudioControls, useAudioElementState } from '../AudioPlayer'
import { nextPlaybackRate } from '../AudioPlayer/utils'
import { Avatar } from '../Avatar'
import { ScreenContext } from '../../contexts/ScreenContext'
import { useSettingsStore } from '../../stores/settings'
import { useRpcFetch } from '../../hooks/useFetch'
import { BackendRemote } from '../../backend-com'
import useMessage from '../../hooks/chat/useMessage'
import { saveLastChatId } from '../../backend/chat'
import { runtime } from '@deltachat-desktop/runtime-interface'
import { getLogger } from '@deltachat-desktop/shared/logger'
import {
  mediaSessionMetadata,
  clampSeek,
  voicePlayerSurface,
} from './mediaSession'

const log = getLogger('GlobalVoiceMessagePlayer')

export function GlobalVoiceMessagePlayer({
  accountId: selectedAccountId,
  strip,
}: {
  /** The currently selected account, to decide same- vs cross-account jump. */
  accountId: number | undefined
  /**
   * This instance is the under-navbar strip mount point (small-screen layout),
   * not the chat-list column one. Both are mounted; the one that isn't the
   * current surface renders `null` and, crucially, keeps all its
   * fetch/media-session hooks idle, so none of that work happens twice.
   */
  strip?: boolean
}) {
  const tx = useTranslationFunction()

  const mediaPlayerMutexCtx = useContext(MediaPlayerMutexContext)
  const nextVoiceMessagePlayerCtx = useContext(NextVoiceMessagePlayerContext)
  const { smallScreenMode } = useContext(ScreenContext)

  const desktopSettings = useSettingsStore()[0]?.desktopSettings
  const customControlsEnabled =
    desktopSettings?.experimentalAudioPlayerControls === true
  const showDetails = desktopSettings?.voiceMessageMediaSessionDetails === true

  const currentSrc = mediaPlayerMutexCtx.currentSrc
  const currMessage = nextVoiceMessagePlayerCtx.currMessage

  const surface = voicePlayerSurface(
    smallScreenMode,
    customControlsEnabled,
    currentSrc != null
  )
  // The strip mounts above the chat-list/chat-view switch (it must survive
  // both), the bar and the native element inside the chat-list column.
  const isOurSurface = strip
    ? surface === 'strip'
    : surface === 'bar' || surface === 'native'

  // Wrap the never-changing singleton in a stable ref object so
  // CustomAudioControls' effects (keyed on `audioRef`) don't re-subscribe on
  // every render of this component.
  const singletonRef = useMemo(
    () => ({ current: mediaPlayerMutexCtx.audioElement }),
    [mediaPlayerMutexCtx.audioElement]
  )

  // Sender + chat for the mini-player's clickable label (once per message).
  // Only fetched with the custom player on, so the native path is untouched,
  // and only by the instance that actually renders, so the idle mount point
  // costs nothing.
  const active = isOurSurface && customControlsEnabled && currMessage != null
  // ponytail: on auto-advance this refetches the message the context just
  // fetched as `nextMessage` (one duplicate local getMessage per advance; the
  // bubble-initiated first message has no prefetched copy, so the fetch must
  // exist regardless). Upgrade path: expose the prefetched T.Message alongside
  // currMessage on the context and prefer it here if the duplicate RPC matters.
  const messageFetch = useRpcFetch(
    BackendRemote.rpc.getMessage,
    active ? [currMessage.accountId, currMessage.messageId] : null
  )
  const chatFetch = useRpcFetch(
    BackendRemote.rpc.getBasicChatInfo,
    active ? [currMessage.accountId, currMessage.chatId] : null
  )
  // `result` (not `lingeringResult`): while the new message's fetch is in
  // flight these must be null, so the display can never attribute the playing
  // message to the previous sender/chat (blank-while-loading beats
  // wrong-while-loading, e.g. across an auto-advance).
  const message = messageFetch?.result?.ok ? messageFetch.result.value : null
  const chat = chatFetch?.result?.ok ? chatFetch.result.value : null

  const senderName =
    (message && (message.overrideSenderName || message.sender.displayName)) ||
    ''
  const chatName = chat?.name ?? ''
  // In a 1:1 chat the chat is named after the contact, so the sub-line would
  // just repeat the sender ("Bob Martinez / Bob Martinez"). Show it only when
  // it says something new (groups, and "Me / Saved Messages").
  const showChatName = chatName !== '' && chatName !== senderName
  const avatarPath = message?.sender.profileImage ?? null
  const avatarUrl = avatarPath
    ? runtime.transformBlobURL(avatarPath)
    : undefined

  // Media session (OS/lock-screen controls). Suppressed metadata by default;
  // details only with the opt-in setting on.
  const meta = useMemo(
    () =>
      // Suppress details until the current message's fetch resolves, so the OS
      // controls never show a stale/empty title during an auto-advance window.
      mediaSessionMetadata(
        !showDetails || message == null,
        tx('voice_message'),
        {
          senderName,
          chatName,
          avatarUrl,
        }
      ),
    [showDetails, message, tx, senderName, chatName, avatarUrl]
  )
  useMediaSession(
    // Belongs to whichever instance is mounted — the idle one must not
    // register (or, on a breakpoint change, clear) the OS handlers.
    isOurSurface && customControlsEnabled,
    mediaPlayerMutexCtx.audioElement,
    meta,
    // No next-track when there's no queued message (e.g. draft audio preview):
    // an unregistered handler makes the OS hide the button instead of offering
    // a dead one.
    currMessage != null ? nextVoiceMessagePlayerCtx.playNext : null
  )

  const { jumpToMessage } = useMessage()

  // `currentSrc == null` is already `surface === 'none'`; repeated for the
  // type narrowing below.
  if (!isOurSurface || currentSrc == null) {
    return null
  }

  const onJumpToMessage = () => {
    const m = nextVoiceMessagePlayerCtx.currMessage
    if (m == null) {
      return
    }
    if (m.accountId === selectedAccountId) {
      jumpToMessage({
        accountId: m.accountId,
        msgId: m.messageId,
        msgChatId: m.chatId,
        focus: true,
        scrollIntoViewArg: { block: 'center' },
      })
      return
    }
    // ponytail: cross-account jump mirrors CommandPalette's idiom (jumpToMessage
    // can't cross accounts). Rare — reachable only when the singleton keeps
    // playing after the user switches accounts. Upgrade path: factor
    // CommandPalette's helper out if a third caller ever needs it.
    saveLastChatId(m.accountId, m.chatId)
      .then(() => window.__selectAccount(m.accountId))
      .then(() => {
        window.__internal_jump_to_message_asap = {
          accountId: m.accountId,
          chatId: m.chatId,
          jumpToMessageArgs: [
            {
              msgId: m.messageId,
              focus: true,
              scrollIntoViewArg: { block: 'center' },
            },
          ],
        }
        window.__internal_check_jump_to_message?.()
      })
      .catch(err => log.error('failed to jump to voice message', err))
  }

  const onClose = () => {
    mediaPlayerMutexCtx.stop()
    // Functionality-wise this is not necessary, but let's do it
    // for good measure.
    nextVoiceMessagePlayerCtx.setCurrMessage(null)
  }

  const closeButton = (
    <button
      className={styles.closeButton}
      type='button'
      aria-label={tx('close')}
      onClick={onClose}
    >
      <Icon className={styles.closeButtonIcon} icon={'cross'} size={26} />
    </button>
  )

  // Small-screen layout: a slim strip under the navbar, the one surface that
  // is visible both in the chat list and inside a chat (#137).
  if (surface === 'strip') {
    return (
      <VoiceMessageStrip
        audioRef={singletonRef}
        // Same guard as the bar's sender line: no dead button while there is
        // nothing to jump to (draft preview, in-flight auto-advance fetch).
        label={
          currMessage != null && message != null
            ? {
                text: showChatName ? `${senderName} · ${chatName}` : senderName,
                onClick: onJumpToMessage,
              }
            : null
        }
        onClose={onClose}
      />
    )
  }

  // Custom mini-player: sender line + singleton-driven controls, in place of
  // re-parenting the native <audio> singleton into the bar.
  if (surface === 'bar') {
    return (
      <section
        className={classNames(styles.root, styles.rootCustom)}
        aria-label={tx('audio')}
      >
        {/*
          Only render the clickable sender row once there's a message to jump
          to AND its fetch resolved — otherwise (draft audio preview, or an
          in-flight auto-advance fetch) this would be an empty focusable button
          whose activation does nothing. Controls + close still render.
        */}
        {currMessage != null && message != null && (
          <button
            className={styles.senderLine}
            type='button'
            onClick={onJumpToMessage}
            title={tx('jump_to_message')}
          >
            <Avatar
              avatarPath={avatarPath}
              color={message.sender.color}
              displayName={senderName}
              addr={message.sender.address}
              small
              aria-hidden={true}
            />
            <span className={styles.senderText}>
              <span className={styles.senderName}>{senderName}</span>
              {showChatName && (
                <span className={styles.chatName}>{chatName}</span>
              )}
            </span>
          </button>
        )}
        <div className={styles.controlsRow}>
          {/*
            Drive THE SINGLETON directly (not a muted proxy): play/pause/seek/
            rate act on the element that produces sound. A2's restore guard
            (`!globalEl.paused && src matches → skip`) is a no-op for live
            playback here because audioRef.current IS ctx.audioElement, so it
            never rewinds the message that's currently playing.
          */}
          <CustomAudioControls
            audioRef={singletonRef}
            src={currentSrc}
            compact
          />
          {closeButton}
        </div>
      </section>
    )
  }

  // Native path (setting off): unchanged — re-parent the singleton controls.
  return (
    <section className={styles.root} aria-label={tx('audio')}>
      <div
        className={styles.audioElContainer}
        ref={el => {
          if (el == undefined) {
            return
          }
          // Don't append if already appended.
          // Otherwise the submenus of the audio element would close,
          // e.g. "playback rate".
          if (el.contains(mediaPlayerMutexCtx.audioElement)) {
            return
          }
          el.append(mediaPlayerMutexCtx.audioElement)
        }}
      />
      {closeButton}
    </section>
  )
}

/**
 * The small-screen surface: one row (play/pause, label, speed, close) over a
 * 2px progress hairline, pinned under the navbar.
 *
 * Its own component so the ~4Hz `timeupdate` re-renders stay here instead of
 * re-rendering the whole player (and its fetch hooks).
 */
function VoiceMessageStrip({
  audioRef,
  label,
  onClose,
}: {
  audioRef: React.RefObject<HTMLAudioElement | null>
  /** Jump-to-message label, or `null` when there's no message to jump to. */
  label: { text: string; onClick: () => void } | null
  onClose: () => void
}) {
  const tx = useTranslationFunction()
  const ctx = useContext(MediaPlayerMutexContext)
  const { paused, progress } = useAudioElementState(audioRef)

  return (
    <section
      className={styles.strip}
      aria-label={tx('audio')}
      data-testid='voice-message-strip'
    >
      <div className={styles.stripRow}>
        <IconButton
          icon={paused ? 'play' : 'pause'}
          coloring='currentColor'
          size={18}
          className={styles.stripPlayButton}
          aria-label={paused ? tx('menu_play') : tx('menu_pause')}
          onClick={() => {
            const el = audioRef.current
            if (el == null) {
              return
            }
            // This IS the singleton: no position restore needed (unlike a cold
            // bubble element), its currentTime is the live one.
            if (el.paused) {
              el.play()
            } else {
              el.pause()
            }
          }}
        />
        {label != null ? (
          <button
            type='button'
            className={styles.stripLabel}
            title={tx('jump_to_message')}
            onClick={label.onClick}
          >
            {label.text}
          </button>
        ) : (
          <span className={styles.stripSpacer} />
        )}
        <button
          type='button'
          className={styles.speedPill}
          aria-label={tx('a11y_playback_speed', `${ctx.playbackRate}×`)}
          onClick={() =>
            ctx.setPlaybackRate(nextPlaybackRate(ctx.playbackRate))
          }
        >
          {ctx.playbackRate}×
        </button>
        <IconButton
          icon='cross'
          coloring='currentColor'
          size={18}
          aria-label={tx('close')}
          onClick={onClose}
        />
      </div>
      {/*
        Position readout only — the bubble's waveform and the OS media session
        own seeking, so there is deliberately no pointer target here (a 2px
        strip would be a poor one anyway). Decorative: the same position is
        already announced by the bubble's seek slider.
      */}
      <div className={styles.hairline} aria-hidden={true}>
        <div
          className={styles.hairlineFill}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </section>
  )
}

// The media-session actions we own; cleared together when playback stops.
const MEDIA_SESSION_ACTIONS: MediaSessionAction[] = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'seekto',
  'nexttrack',
]

/**
 * Wire `navigator.mediaSession` to the global singleton while `active`. Clears
 * metadata + handlers when `active` goes false / on unmount / setting off.
 */
function useMediaSession(
  active: boolean,
  audioEl: HTMLAudioElement,
  meta: { title: string; artist: string; artwork: { src: string }[] },
  onPlayNext: (() => void) | null
): void {
  const title = meta.title
  const artist = meta.artist
  const artworkSrc = meta.artwork[0]?.src ?? ''
  // `null` when there's no queued message: don't register a nexttrack handler.
  const nextEnabled = onPlayNext != null
  // Effect Event: the nexttrack handler reads the latest `onPlayNext` without
  // re-running the effect (its identity changes as currMessage changes).
  const onNext = useEffectEvent(() => onPlayNext?.())

  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return
    }
    const ms = navigator.mediaSession

    const clearHandlers = () => {
      for (const a of MEDIA_SESSION_ACTIONS) {
        try {
          ms.setActionHandler(a, null)
        } catch {
          // Some browsers throw for actions they don't support.
        }
      }
    }

    // Side-effect-free when idle: with both the bar and the strip mounted
    // (breakpoint crossing mid-playback), the newly-idle instance's setup runs
    // AFTER the newly-active one's — clearing here would wipe the fresh
    // registration. The previously-active run's cleanup (below) already
    // clears, and all cleanups run before any setup.
    if (!active) {
      return
    }

    ms.metadata = new MediaMetadata({
      title,
      artist,
      artwork: artworkSrc ? [{ src: artworkSrc }] : [],
    })

    const SEEK = 10
    const setTime = (t: number) => {
      audioEl.currentTime = t
      // Bypass the 1Hz throttle so the OS scrubber reflects the seek at once
      // instead of showing the pre-seek position for up to a second.
      // (`lastPos`/`updatePos` close over the effect scope, initialised by the
      // time any media-session handler can fire.)
      lastPos = 0
      updatePos()
    }
    const handlers: Partial<
      Record<MediaSessionAction, MediaSessionActionHandler>
    > = {
      play: () => {
        audioEl.play()
      },
      pause: () => {
        audioEl.pause()
      },
      seekbackward: d =>
        setTime(
          clampSeek(
            audioEl.currentTime,
            -(d.seekOffset ?? SEEK),
            audioEl.duration
          )
        ),
      seekforward: d =>
        setTime(
          clampSeek(audioEl.currentTime, d.seekOffset ?? SEEK, audioEl.duration)
        ),
      seekto: d => {
        if (d.seekTime != null) {
          setTime(d.seekTime)
        }
      },
      nexttrack: nextEnabled ? () => onNext() : undefined,
    }
    for (const a of MEDIA_SESSION_ACTIONS) {
      try {
        ms.setActionHandler(a, handlers[a] ?? null)
      } catch {
        // Unsupported action in this browser — skip it.
      }
    }

    // positionState, throttled to ~1Hz. setPositionState needs a finite
    // duration with position in [0, duration] and throws otherwise.
    let lastPos = 0
    const updatePos = () => {
      const now = Date.now()
      if (now - lastPos < 1000) {
        return
      }
      const dur = audioEl.duration
      if (!Number.isFinite(dur) || dur <= 0) {
        return
      }
      lastPos = now
      try {
        ms.setPositionState({
          duration: dur,
          position: Math.min(Math.max(audioEl.currentTime, 0), dur),
          playbackRate: audioEl.playbackRate || 1,
        })
      } catch {
        // setPositionState can still throw (e.g. an infinite duration slips
        // past the guard on some engines) — position is best-effort.
      }
    }
    updatePos()
    const posEvents = ['timeupdate', 'ratechange', 'durationchange'] as const
    posEvents.forEach(e => audioEl.addEventListener(e, updatePos))

    return () => {
      posEvents.forEach(e => audioEl.removeEventListener(e, updatePos))
      ms.metadata = null
      clearHandlers()
    }
  }, [active, audioEl, title, artist, artworkSrc, nextEnabled])
}
